<template>
  <div>
    <v-card class="mb-4">
      <v-card-text class="pb-2">
        <div class="d-flex align-center flex-wrap mb-3">
          <div class="me-4">
            <div class="text-h6">
              Категории
            </div>
            <div class="text-caption text--secondary">
              {{ categoriesSummary }}
            </div>
          </div>

          <v-spacer></v-spacer>

          <v-text-field
            v-model="categoryQuery"
            dense
            outlined
            hide-details
            clearable
            placeholder="Найти категорию..."
            class="category-search me-3"
            :prepend-inner-icon="icons.mdiMagnify"
          ></v-text-field>

          <v-switch
            v-model="hideEmpty"
            dense
            hide-details
            inset
            label="Только с блюдами"
            class="mt-0 me-3"
          ></v-switch>

          <v-btn
            color="primary"
            :disabled="hasNotPermission('Permissions.MenuCategoryUpdate')"
            @click="createDialogShow()"
          >
            <v-icon
              size="18"
              class="me-1"
            >
              {{ icons.mdiPlus }}
            </v-icon>
            <span>Категория</span>
          </v-btn>
        </div>

        <div class="category-chips">
          <v-chip
            v-for="item in visibleCategories"
            :key="item.id"
            class="ma-1"
            :color="item.id === menuCategory ? 'primary' : undefined"
            :outlined="item.id !== menuCategory"
            :title="item.path || item.name"
            @click="selectCategory(item)"
          >
            <v-icon
              v-if="item.isCustom"
              left
              size="16"
            >
              {{ icons.mdiStar }}
            </v-icon>
            {{ item.name }}
            <span class="ms-2 text-caption category-count">{{ item.dishCount }}</span>
            <v-icon
              right
              size="16"
              @click.stop="updateDialogShow(item.id)"
            >
              {{ icons.mdiPencil }}
            </v-icon>
          </v-chip>

          <div
            v-if="!visibleCategories.length"
            class="text-caption text--secondary pa-2"
          >
            Ничего не нашлось
          </div>
        </div>
      </v-card-text>
    </v-card>

    <dish-list
      v-if="menuCategory"
      :menu-version="menuVersionId"
      :menu-category="menuCategory"
      :category-name="selectedName"
    ></dish-list>

    <v-card v-else>
      <v-card-text class="text-center py-12 text--secondary">
        <v-icon
          size="48"
          class="mb-3"
        >
          {{ icons.mdiFoodForkDrink }}
        </v-icon>
        <div>Выберите категорию, чтобы увидеть блюда</div>
      </v-card-text>
    </v-card>

    <menu-category-form
      :label="'Редактирование категории меню'"
      :cancellation="updateDialogCancel"
      :show="updateDialog"
      :menu-version="params"
      :save="updateMenuCategory"
    ></menu-category-form>
    <menu-category-form
      :label="'Создание категории меню'"
      :cancellation="createDialogCancel"
      :show="createDialog"
      :save="createMenuCategory"
      :menu-version="params"
    ></menu-category-form>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  mdiPencil, mdiPlus, mdiMagnify, mdiStar, mdiFoodForkDrink,
} from '@mdi/js'
import DishList from './dish-list.vue'
import { mapFormHandlers } from '@/helpers/forms'
import MenuCategoryForm from '@/views/menu-version/menu-category-form'
import { hasPermission } from '@/helpers'

export default {
  name: 'MenuCategoryList',
  components: { MenuCategoryForm, DishList },
  data: () => ({
    createDialog: false,
    updateDialog: false,
    menuCategory: 0,
    menuVersionId: 0,
    categoryQuery: '',
    hideEmpty: true,
    icons: {
      mdiPencil, mdiPlus, mdiMagnify, mdiStar, mdiFoodForkDrink,
    },
  }),
  computed: {
    ...mapState('menuCategory', {
      menuCategories: 'list',
    }),
    ...mapFormHandlers('menuCategory', ['params']),

    visibleCategories() {
      const query = (this.categoryQuery || '').trim().toLowerCase()

      return this.menuCategories.filter(c => {
        if (this.hideEmpty && !c.dishCount) return false
        if (!query) return true

        return `${c.name} ${c.path || ''}`.toLowerCase().includes(query)
      })
    },

    categoriesSummary() {
      const total = this.menuCategories.length
      const filled = this.menuCategories.filter(c => c.dishCount).length
      const dishes = this.menuCategories.reduce((sum, c) => sum + (c.dishCount || 0), 0)

      return `${total} всего, ${filled} с блюдами, ${dishes} блюд`
    },

    selectedName() {
      return this.menuCategories.find(c => c.id === this.menuCategory)?.name || ''
    },
  },
  watch: {
    menuCategories: {
      immediate: true,
      handler(list) {
        if (this.menuCategory || !list || !list.length) return

        const fromRoute = list.find(c => c.id === Number(this.$route.query.category))
        const first = fromRoute || list.find(c => c.dishCount) || list[0]
        if (first) this.menuCategory = first.id
      },
    },
  },
  methods: {
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    ...mapActions('menuCategory', {
      fetchMenuCategories: 'fetchList',
    }),
    ...mapActions('menuCategory/form', ['create', 'resetForm', 'fetchById', 'update']),
    selectCategory(item) {
      this.menuCategory = item.id

      this.$router.replace({ query: { ...this.$route.query, category: String(item.id) } }).catch(() => {})
    },
    createDialogShow() {
      this.createDialog = true
    },
    createDialogCancel() {
      this.createDialog = false
    },
    updateDialogCancel() {
      this.updateDialog = false
    },
    updateDialogShow(id) {
      this.fetchById(id)
      this.updateDialog = true
    },
    async createMenuCategory() {
      await this.create()
      this.createDialog = false
      await this.fetchMenuCategories()
    },
    async updateMenuCategory() {
      await this.update()
      this.updateDialog = false
      await this.fetchMenuCategories()
    },
  },
  async created() {
    document.title = 'Меню'
    const { menuVersionId } = this.$route.params
    this.menuVersionId = menuVersionId
    this.params = menuVersionId
    await this.fetchMenuCategories()
  },
}
</script>

<style lang="scss" scoped>
.category-search {
  max-width: 16rem;
}

.category-chips {
  max-height: 13rem;
  overflow-y: auto;
}

.category-count {
  opacity: 0.7;
}
</style>
